import type { Recipe, ShoppingCategory, ShoppingListItem, ShoppingSelection } from "./types";

export const SHOPPING_STORAGE_KEY = "ayurvedon.shopping.v1";

export const SHOPPING_CATEGORY_ORDER: ShoppingCategory[] = ["produce", "chilled", "legumes", "pantry", "spices", "toppings"];

export interface StoredShoppingState {
  selections: ShoppingSelection[];
  checked: string[];
}

export function buildShoppingList(recipes: Recipe[], selections: ShoppingSelection[]): ShoppingListItem[] {
  const result = new Map<string, ShoppingListItem>();

  selections.forEach((selection) => {
    const recipe = recipes.find((candidate) => candidate.slug === selection.recipeSlug);
    if (!recipe || selection.people < 1) return;
    const factor = selection.people / recipe.servings;

    (recipe.shoppingIngredients ?? []).forEach((ingredient) => {
      const aggregateKey = `${ingredient.key}:${ingredient.unit}`;
      const existing = result.get(aggregateKey);
      const scaledAmount = ingredient.amount * factor;
      if (existing) {
        existing.amount += scaledAmount;
        if (!existing.sourceRecipes.includes(recipe.title)) existing.sourceRecipes.push(recipe.title);
      } else {
        result.set(aggregateKey, { ...ingredient, amount: scaledAmount, sourceRecipes: [recipe.title] });
      }
    });
  });

  return [...result.values()].sort((left, right) => {
    const categoryDifference = SHOPPING_CATEGORY_ORDER.indexOf(left.category) - SHOPPING_CATEGORY_ORDER.indexOf(right.category);
    return categoryDifference || left.name.localeCompare(right.name, "de");
  });
}

export function formatShoppingAmount(amount: number, unit: ShoppingListItem["unit"]): string {
  const purchaseAmount = unit === "Stück" ? Math.ceil(amount) : amount;
  const digits = purchaseAmount < 10 && !Number.isInteger(purchaseAmount) ? 1 : 0;
  return `${new Intl.NumberFormat("de-CH", { maximumFractionDigits: digits }).format(purchaseAmount)} ${unit}`;
}

export function shoppingItemId(item: Pick<ShoppingListItem, "key" | "unit">): string {
  return `${item.key}:${item.unit}`;
}

export function loadShoppingState(): StoredShoppingState {
  try {
    const parsed = JSON.parse(localStorage.getItem(SHOPPING_STORAGE_KEY) ?? "null") as StoredShoppingState | null;
    if (!parsed || !Array.isArray(parsed.selections) || !Array.isArray(parsed.checked)) throw new Error("invalid state");
    return {
      selections: parsed.selections.filter((item) => typeof item.recipeSlug === "string" && item.people >= 1 && item.people <= 12),
      checked: parsed.checked.filter((item) => typeof item === "string"),
    };
  } catch {
    return { selections: [], checked: [] };
  }
}

export function saveShoppingState(state: StoredShoppingState): void {
  localStorage.setItem(SHOPPING_STORAGE_KEY, JSON.stringify(state));
}

export function createShareText(items: ShoppingListItem[], checked: Set<string>): string {
  const lines = SHOPPING_CATEGORY_ORDER.flatMap((category) => {
    const categoryItems = items.filter((item) => item.category === category && !checked.has(shoppingItemId(item)));
    if (!categoryItems.length) return [];
    return categoryItems.map((item) => `• ${formatShoppingAmount(item.amount, item.unit)} ${item.name}${item.note ? ` (${item.note})` : ""}`);
  });
  return ["Ayurvedon Einkaufsliste", "", ...lines].join("\n");
}
